import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { z } from 'zod'

import useMultipleSchema from '../../../lib/hooks/use-multiple-schema'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffEndOfStayReportInput } from '../../../lib/types/models/affectedStaff/affected-staff-end-of-stay-report'
import { NullableTransformer } from '../../../lib/types/utils'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { mapEndOfStayQuestions } from '../../../views/staff-personal-space/utils'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'

// Schemas are created inside the component so we can use translated labels (t)

type Props = {
  readOnly?: boolean
  defaultValues?: AffectedStaffEndOfStayReportInput
  handleSubmit: (
    data: AffectedStaffEndOfStayReportInput,
    type: 'submit' | 'save-for-later'
  ) => Promise<void>
}
export default function AffectedStaffEndOfStayReportForm({
  readOnly,
  defaultValues,
  handleSubmit
}: Props) {
  const { t } = useTranslate()

  const saveForLaterSchema: z.ZodType<NullableTransformer<AffectedStaffEndOfStayReportInput>> =
    z.object({
      residenceFacilities: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.residenceFacilities')
      ).nullish(),
      managementTeam: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.managementTeam')
      ).nullish(),
      peersInvolvement: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.peersInvolvement')
      ).nullish(),
      excursionsFeedback: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.excursionsFeedback')
      ).nullish(),
      activitiesEvenings: createStringSchema(
        t(
          'staff.personal.space.my.journeys.details.end.of.stay.questions.activitiesEvenings.professors'
        )
      ).nullish(),
      classroomEquipment: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.classroomEquipment')
      ).nullish(),
      foodQuality: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.foodQuality')
      ).nullish(),
      residenceImprovements: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.residenceImprovements')
      ).nullish(),
      additionalRemarks: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.additionalRemarks')
      ).nullish()
    })

  const submissionSchema: z.ZodType<NullableTransformer<AffectedStaffEndOfStayReportInput>> =
    z.object({
      residenceFacilities: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.residenceFacilities')
      ),
      managementTeam: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.managementTeam')
      ),
      peersInvolvement: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.peersInvolvement')
      ),
      excursionsFeedback: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.excursionsFeedback')
      ),
      activitiesEvenings: createStringSchema(
        t(
          'staff.personal.space.my.journeys.details.end.of.stay.questions.activitiesEvenings.professors'
        )
      ),
      classroomEquipment: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.classroomEquipment')
      ),
      foodQuality: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.foodQuality')
      ),
      residenceImprovements: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.residenceImprovements')
      ),
      additionalRemarks: createStringSchema(
        t('staff.personal.space.my.journeys.details.end.of.stay.questions.additionalRemarks')
      ).nullish()
    })

  const { form, loading, submitForm } = useMultipleSchema<
    ['save-for-later', 'submit'],
    AffectedStaffEndOfStayReportInput
  >(
    [
      { name: 'save-for-later', schema: saveForLaterSchema },
      { name: 'submit', schema: submissionSchema }
    ],
    handleSubmit,
    defaultValues
  )

  const questions = mapEndOfStayQuestions(t)

  return (
    <Form form={form}>
      <form className="flex flex-col gap-4">
        {Object.keys(questions).map((key, index) => {
          const value = questions[key as keyof typeof questions]
          return (
            <Box style="border" className="rounded gap-3" key={key}>
              <Badge
                size="flexible"
                className="p-2"
                variant="primary"
                text={
                  <div className="flex gap-0.5">
                    <span>{index + 1}</span>
                    {typeof value === 'string' ? (
                      <span> - {value}</span>
                    ) : (
                      <div>
                        {value.map((part, partIndex) => (
                          <span className="block" key={partIndex}>
                            - {part}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                }
              />

              {readOnly ? (
                <Badge
                  size="flexible"
                  className="p-2"
                  text={defaultValues?.[key as keyof AffectedStaffEndOfStayReportInput] || 'N/A'}
                />
              ) : (
                <FormField
                  control={form.control}
                  name={key as keyof AffectedStaffEndOfStayReportInput}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput {...field} type="textarea" placeholder="Réponse ici" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </Box>
          )
        })}
        {!readOnly && (
          <div className="flex justify-end mt-4 gap-2.5 mobile:flex-col mobile:gap-2">
            <Button
              label="Enregistrer et continuer plus tard"
              variant="ghost"
              type="button"
              loading={loading['save-for-later']}
              onClick={() => {
                submitForm('save-for-later')
              }}
            />
            <Button
              label="Valider et envoyer à LEC"
              variant="primary"
              type="button"
              loading={loading.submit}
              onClick={() => {
                submitForm('submit')
              }}
            />
          </div>
        )}
      </form>
    </Form>
  )
}
